import React, { useState, useMemo } from 'react';
// utils
import { debounce } from 'lodash';
import { getMissionProgress, getPartProgress } from 'store/actions/classroom';
import { useDispatch, useSelector } from 'react-redux';
// components
import NoDataBanner from 'components/DismissBanner/NoDataBanner';
import PartProgressModal from 'components/instances/PartProgressModal';
import Table from 'components/Table';
import OverviewBanner from 'components/OverviewBanner';
import ReloadButton from 'components/Button/ReloadButton';
import Typography from 'components/Typography';
// icons
import { ReactComponent as AlertIcon } from 'icons/alert-icon.svg';
// styles
import classnames from 'classnames/bind';
import styles from './Overview.module.scss';

const cx = classnames.bind(styles);

const Overview = () => {
  const [progressTarget, setProgressTarget] = useState({});
  const dispatch = useDispatch();
  const {
    total_students: totalStudents,
    students_finished: studentsFinished,
    average_overall_score: overallScore,
    parts: progressParts = [],
    missionId,
    hasStarted,
  } = useSelector(({ classroom: { missionProgress = {}, currentMission = {} } }) => {
    const parsedParts = (missionProgress?.parts || []).map((part, idx) => ({ ...part, ...currentMission?.parts[idx] }));

    const hasStarted = parsedParts.filter(({ number }) => number === 0)[0]?.students_started !== 0;

    return {
      ...missionProgress,
      parts: parsedParts,
      missionId: currentMission.id,
      hasStarted,
    };
  });
  const { id: classId } = useSelector(({ classroom }) => classroom);

  const handleReloadClick = debounce(() => dispatch(getMissionProgress(classId, missionId)), 500);
  const fetchPartProgressData = (partId) => dispatch(getPartProgress(classId, missionId, partId));
  const handlePartClick = (partData) => {
    fetchPartProgressData(partData.id);
    setProgressTarget(partData);
  };

  const noDataYet = !hasStarted;

  const secondRenderer = ({ students_started }) => (
    <Typography type="h5">{noDataYet ? '--' : students_started}</Typography>
  );
  const thirdRenderer = ({ students_finished }) => (
    <Typography type="h5">{noDataYet ? '--' : students_finished}</Typography>
  );
  const fourthRenderer = ({ average_class_score }) => (
    <Typography type="h5">
      {noDataYet ? (
        '--'
      ) : typeof average_class_score === 'number' ? (
        <span className={cx('avg-class-score', { alert: +average_class_score / 100 < 0.5 })}>
          {average_class_score}%{+average_class_score / 100 < 0.5 && <AlertIcon className="mgb-10" />}
        </span>
      ) : (
        '--'
      )}
    </Typography>
  );
  const sectionRenderer = useMemo(
    () =>
      ({ id, title, ...rest }) =>
        (
          <Typography type="h5" onClick={() => handlePartClick({ id, title, ...rest })}>
            {title}
          </Typography>
        ),
    [classId, missionId],
  );

  const columns = useMemo(
    () => [
      {
        name: 'Section',
        width: 400,
        renderer: sectionRenderer,
      },
      { name: 'Students Started', width: 225, renderer: secondRenderer },
      {
        name: 'Students Finished',
        width: 225,
        renderer: thirdRenderer,
      },
      { name: 'Average Class Score', width: 150, renderer: fourthRenderer },
    ],
    [classId, missionId, noDataYet],
  );

  return (
    <div className={cx('container')}>
      {noDataYet && <NoDataBanner className="mgb-40" />}
      <div className={cx('header-wrapper')}>
        <ReloadButton
          title="Click to refresh the data"
          variant="light"
          className="mgr-15"
          onClick={handleReloadClick}
        />
        <Typography type="h4">Class Overview</Typography>
      </div>
      <OverviewBanner
        className="mgt-20"
        totalStudents={totalStudents}
        studentsFinished={studentsFinished}
        overallScore={overallScore}
      />
      <Table columns={columns} data={progressParts} className="mgt-40" />
      <PartProgressModal
        refetchData={debounce(fetchPartProgressData, 500)}
        progressTarget={progressTarget}
        hasData={!noDataYet}
        onClose={() => setProgressTarget({})}
      />
    </div>
  );
};

export default Overview;
